<script setup lang="ts">
import type { RouteLocationRaw } from "vue-router";
import type { ObservationResource } from "@carnet/api/backoffice";

const props = defineProps<{
  observation: ObservationResource;
  to: RouteLocationRaw;
  alt: string;
}>();

const { t } = useI18n();

const siteName = computed(() => (props.observation.site as { id: number; name?: string } | null)?.name);
const formattedDate = computed(() => formatObservationDate(props.observation.observationDate));
const title = computed(() =>
  siteName.value
    ? t("observations.tile.title.withSite", { date: formattedDate.value, site: siteName.value })
    : t("observations.tile.title.noSite", { date: formattedDate.value })
);
const currentMemberId = useCurrentMemberId();
const isMine = computed(() => currentMemberId.value != null && currentMemberId.value === String(props.observation.participation.member.id));
const isPendingReview = computed(() => {
  const s = props.observation.validation_status;
  return s === "waiting_for_validation" || s === "in_review" || s === "stand_by";
});
const authorName = computed(() => {
  const m = props.observation.participation.member;
  return memberFullName(m);
});
</script>

<template>
  <NuxtLink
    :to
    class="flex flex-col gap-4 rounded-[1px] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary in-inverted:focus-visible:outline-white"
  >
    <figure class="aspect-4/3 overflow-hidden bg-elevated">
      <AppImage :src="observation.photo?.url" :alt="alt" variant="species" img-class="size-full object-cover" />
    </figure>

    <div class="flex flex-col gap-1">
      <p v-if="title" class="text-lg leading-6 font-semibold text-blue-700">{{ title }}</p>
      <p v-if="isMine" class="flex items-center gap-2.5 text-sm/normal font-semibold text-primary">
        <UIcon name="icon:user" class="size-4 shrink-0" aria-hidden="true" />
        <span
          >{{ t("observations.tile.sharedByMe") }} <template v-if="isPendingReview">({{ t("observations.tile.waiting") }})</template></span
        >
      </p>
      <p v-else class="text-sm leading-5 font-semibold text-primary">
        {{ t("observations.byAuthor", { name: authorName }) }}
      </p>
    </div>
  </NuxtLink>
</template>
